import React from 'react';
import store from '@store';
import { isAfter, isBefore, isWithinInterval, parseISO } from 'date-fns';

export const getBasket = (products, basket) => {
    basket = basket || store.getState().basket;

    return !!basket?.items
        ? products.reduce((acc, current) => {
              const item = basket?.items?.find(({ id }) => id === current?._id);

              if (item) {
                  const discount = getPublicProductDiscount({
                      price: current?.price,
                      discounts: current?.discounts,
                  });

                  return acc.concat([
                      {
                          ...current,
                          id: current?._id,
                          quantity: item.quantity,
                          total: (current?.price ?? 0) * item.quantity,
                          category: current?.category,
                          discount: !!discount
                              ? {
                                    ...discount,
                                    total:
                                        getProductDiscountPrice({
                                            price: current?.price,
                                            discount,
                                        }) * item.quantity,
                                }
                              : null,
                      },
                  ]);
              }

              return acc;
          }, [])
        : [];
};

// products here is returned from basket products above, always pass the above function to get the basket products
export const getBasketSubtotal = (basketProducts, includePublicDiscount) => {
    return !!basketProducts && Array.isArray(basketProducts) && !!basketProducts?.length
        ? parseFloat(
              basketProducts.reduce(
                  (accumulator, { total, discount }) =>
                      accumulator + (includePublicDiscount ? discount?.total || total : total),
                  0
              ) / 100
          )
        : null;
};

export const getPublicProductDiscount = ({ price, discounts }) => {
    return (
        getPublicApplicableProductDiscount(discounts)
            // map through each discount and calculate the new product price
            .map(discount => ({
                _id: discount?._id,
                price: getProductDiscountPrice({ price, discount }),
            }))
            // sort by greatest saving
            .sort((a, b) => (a?.price > b?.price ? -1 : 1))
            // re-attach real discount data, return first discount (best saving) to user.
            .map(discount => discounts.find(({ _id }) => _id === discount?._id))[0]
    );
};

export const getPublicApplicableProductDiscount = discounts => {
    const user = store.getState()?.user;

    return (discounts || [])?.filter(
        ({ startDate, endDate, accountTypes }) =>
            // discount doesn't have start and end date
            ((!startDate && !endDate) ||
                // discount has startDate but no endDate and current date is after startDate
                (!!startDate && !endDate && isAfter(new Date(), parseISO(startDate))) ||
                // discount has endDate but no startDate and current date is before endDate
                (!startDate && !!endDate && isBefore(new Date(), parseISO(endDate))) ||
                // discount has startDate and endDate and current date is within startDate and endDate
                (!!startDate &&
                    !!endDate &&
                    isWithinInterval(new Date(), {
                        start: parseISO(startDate),
                        end: parseISO(endDate),
                    }))) &&
            // discount doesn't have accountTypes (user applicability)
            (!accountTypes ||
                // discount has accountTypes and user exists and user type is appicable to this discount
                (!!user && !!accountTypes && accountTypes.includes(user?.type)))
    );
};

export const getProductDiscountPrice = ({ price, discount: { amount, type } = {} }) => {
    let discountPrice;

    switch (type) {
        case 'percentageOff': {
            discountPrice = parseFloat(price - price * amount);
            break;
        }
        case 'amountOff': {
            discountPrice = parseFloat(price - amount);
            break;
        }
        case 'newAmount': {
            discountPrice = parseFloat(amount);
            break;
        }
    }

    return discountPrice;
};

export const getProductDiscountAccountType = ({ accountTypes, discount }) => {
    if (!accountTypes || !discount) return null;

    const { accountTypes: discountAccountTypes } = discount || {};
    const user = store.getState()?.user;

    return (
        (!!user &&
            !!discountAccountTypes &&
            discountAccountTypes.includes(user?.type) &&
            accountTypes?.find(({ _id }) => _id === user?.type)) ||
        null
    );
};

export const getProductDiscountName = ({ quantity, price, discount }) => {
    if (!discount) return null;

    const { amount: discountPrice, type } = discount || {};
    const value = amount => amount.toFixed(2).replace(/\.?0+$/, '');

    return (
        (type === 'percentageOff' && `${value(discountPrice * 100)}% Off`) ||
        (type === 'amountOff' && `$${value((discountPrice * (quantity || 1)) / 100)} Off`) ||
        (type === 'newAmount' &&
            `$${value((price * (quantity || 1) - discountPrice * (quantity || 1)) / 100)} Off`) ||
        null
    );
};

export const getDiscountName = discount => {
    if (!discount) return null;

    const { amount, type } = discount || {};
    const value = amount => amount.toFixed(2).replace(/\.?0+$/, '');

    return (
        (type === 'percentageOff' && `${value(amount * 100)}% Off`) ||
        (type === 'amountOff' && `$${value(amount)} Off`) ||
        (type === 'newAmount' && `Now $${value(amount)}`) ||
        null
    );
};

export const getDiscountSubtotal = ({ subtotal, discount }) => {
    if (!discount) return subtotal;

    const { type, amount } = discount;

    if (type === 'percentageOff') {
        return subtotal - subtotal * amount < 0 ? 0 : subtotal - subtotal * amount;
    }

    if (type === 'amountOff') {
        return subtotal - amount < 0 ? 0 : subtotal - amount;
    }

    if (type === 'newAmount') {
        return amount < 0 ? 0 : amount;
    }

    return null;
};
